import type { TCreateSlice } from "@/store";
import type {
    TExtractionInfoWithParent,
    TFileInfoWithParent,
} from "./dealFileExtractionTypes";
import type { FileExtractionInfo } from "@/ui/organisms/dealTypes";
import type { UserType } from "@/ui/organisms/userTypes";
import { canDeleteExtraction, canDeleteFile } from "@/ui/organisms/userTypes";
import { isFileInfoWithParentTypeGuard } from "@/ui/organisms/DealOverview/DealProcessedFiles";
import { isExtractionInfoWithParentTypeGuard } from "@/ui/organisms/DealOverview/DealProcessedFiles";

type TStateDealFileExtractionSelection = {
    selectedFiles: TFileInfoWithParent[];
    selectedExtractions: TExtractionInfoWithParent[];
};
type TActionsPublicDealFileExtractionSelection = {
    toggleExtraction: (extraction: TExtractionInfoWithParent) => void;
    clearSelection: () => void;
    setSelectedFilesAndExtractions: (
        files: TFileInfoWithParent[],
        extractions: TExtractionInfoWithParent[],
    ) => void;
    getSelectedStatus: (
        fileExtractionInfo: TFileInfoWithParent | TExtractionInfoWithParent,
    ) => "selected" | "intermediate" | "unselected";
    setFileAndChildren: (
        files: TFileInfoWithParent[],
        setTo: boolean,
        exclusive: boolean,
        fileExtractionInfo: FileExtractionInfo[],
    ) => void;
};

type TDerivedStateDealFileExtractionSelection = {
    getSelectedDeletableExtractions: ({
        userType,
    }: {
        userType: UserType;
    }) => TExtractionInfoWithParent[];
    getSelectedDeletableFiles: ({
        userType,
    }: {
        userType: UserType;
    }) => TFileInfoWithParent[];
};

export type TSliceDealFileExtractionSelection =
    TStateDealFileExtractionSelection &
        TActionsPublicDealFileExtractionSelection &
        TDerivedStateDealFileExtractionSelection;

export const initialStateDealFileExtractionSelection = {
    selectedFiles: [],
    selectedExtractions: [],
} as const satisfies TStateDealFileExtractionSelection;

export const createSliceDealFileExtractionSelection: TCreateSlice<
    TSliceDealFileExtractionSelection
> = () => (set, get) => {
    const actionsPublic: TActionsPublicDealFileExtractionSelection = {
        setFileAndChildren: (
            files: TFileInfoWithParent[],
            setTo: boolean,
            exclusive: boolean,
            fileExtractionInfo: FileExtractionInfo[],
        ) => {
            const extractions = fileExtractionInfo
                .filter(fileExtractionInfo =>
                    isExtractionInfoWithParentTypeGuard(fileExtractionInfo),
                )
                .filter(fileExtractionInfo =>
                    files.some(f => f.fileId === fileExtractionInfo.fileId),
                );
            if (setTo) {
                if (exclusive) {
                    actionsPublic.setSelectedFilesAndExtractions(
                        files,
                        extractions,
                    );
                } else {
                    actionsPublic.setSelectedFilesAndExtractions(
                        [
                            ...get().selectedFiles,
                            ...files.filter(
                                f =>
                                    !get().selectedFiles.some(
                                        sf => sf.fileId === f.fileId,
                                    ),
                            ),
                        ],
                        [
                            ...get().selectedExtractions,
                            ...extractions.filter(
                                e =>
                                    !get().selectedExtractions.some(
                                        se =>
                                            se.extractionId === e.extractionId,
                                    ),
                            ),
                        ],
                    );
                }
            } else {
                actionsPublic.setSelectedFilesAndExtractions(
                    get().selectedFiles.filter(
                        f => !files.some(f2 => f2.fileId === f.fileId),
                    ),
                    get().selectedExtractions.filter(
                        e =>
                            !extractions.some(
                                e2 => e2.extractionId === e.extractionId,
                            ),
                    ),
                );
            }
        },
        getSelectedStatus: (
            fileExtractionInfo: TFileInfoWithParent | TExtractionInfoWithParent,
        ) => {
            if (isFileInfoWithParentTypeGuard(fileExtractionInfo)) {
                return get().selectedFiles.some(
                    f => f.fileId === fileExtractionInfo.fileId,
                )
                    ? "selected"
                    : get().selectedExtractions.some(
                            e => e.fileId === fileExtractionInfo.fileId,
                        )
                      ? "intermediate"
                      : "unselected";
            } else if (
                isExtractionInfoWithParentTypeGuard(fileExtractionInfo)
            ) {
                return get().selectedExtractions.some(
                    e => e.extractionId === fileExtractionInfo.extractionId,
                )
                    ? "selected"
                    : "unselected";
            }
            return "unselected";
        },
        clearSelection: () => {
            set(
                { selectedFiles: [], selectedExtractions: [] },
                false,
                "fileSelect/clearSelection",
            );
        },
        setSelectedFilesAndExtractions: (files, extractions) => {
            set(
                {
                    selectedFiles: files,
                    selectedExtractions: extractions,
                },
                false,
                "fileSelect/selectEntireFile",
            );
        },
        toggleExtraction: extraction => {
            const selectedExtractions = get().selectedExtractions;
            if (
                !selectedExtractions.some(
                    e => e.extractionId === extraction.extractionId,
                )
            ) {
                set(
                    {
                        selectedExtractions: [
                            ...selectedExtractions,
                            extraction,
                        ],
                    },
                    false,
                    "fileSelect/addExtraction",
                );
            } else {
                set(
                    {
                        selectedExtractions: selectedExtractions.filter(
                            e => e.extractionId !== extraction.extractionId,
                        ),
                    },
                    false,
                    "fileSelect/removeExtraction",
                );
            }
        },
    };

    const derivedState: TDerivedStateDealFileExtractionSelection = {
        getSelectedDeletableExtractions: ({
            userType,
        }: {
            userType: UserType;
        }) =>
            get().selectedExtractions.filter(e =>
                canDeleteExtraction(userType, e),
            ),
        getSelectedDeletableFiles: ({ userType }: { userType: UserType }) =>
            get().selectedFiles.filter(f => canDeleteFile(userType, f)),
    };

    return {
        ...initialStateDealFileExtractionSelection,
        ...actionsPublic,
        ...derivedState,
    };
};
